import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  pluck,
  catchError,
  map,
  exhaustMap,
  tap
} from 'rxjs/operators';
import { of, Observable, EMPTY } from 'rxjs';
import { BookingActions } from '../actions/booking.actions';
import { BookingService } from '../../services/booking.service';
import { NbToastrService } from '@nebular/theme';
import { Action } from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import { BookingReportType } from '@app/@core/data/models/booking-report.model';

@Injectable()
export class BookingEffects {
  $loadBooking = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.BOOKING_LOAD_BOOKING),
      exhaustMap((payload: { company: string; file: string  }) => {
        return this.loadBookingsData(payload.company,payload.file);
      }),
    ),
  );

  private loadBookingsData(company: string, file: string): Observable<Action> {
    return this.bookingService.getBooking(company, file).pipe(
      pluck('body'),
      map(data => ({
        type: BookingActions.BOOKING_LOAD_BOOKING_SUCCESS,
        payload: { booking: data },
      })),
      catchError(err => {
        this.toastrService.danger('Something went wrong please review your url','Failure')
        return of({
          type: BookingActions.BOOKING_LOAD_BOOKING_FAILURE,
          payload: err,
        });
      })
    );
  }

  $loadBookingDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.BOOKING_LOAD_BOOKING_DETAILS),
      exhaustMap((payload: { bookingId: string; companyId: string}) => {
        return this.loadBookingDetailsData(payload.bookingId, payload.companyId);
      }),
    ),
  );

  private loadBookingDetailsData(bookingId: string,companyId: string): Observable<Action> {
    return this.bookingService.getBookingDetails(bookingId, companyId).pipe(
      pluck('body'),
      map(data => ({
        type: BookingActions.BOOKING_LOAD_BOOKING_DETAILS_SUCCESS,
        payload: { details: data },
      })),
      catchError(err => {
        this.toastrService.danger('Something went wrong please','Failure');
        return of({
          type: BookingActions.BOOKING_LOAD_BOOKING_DETAILS_FAILURE,
          payload: err,
        });
      })
    );
  }

  $loadBookingReport = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.BOOKING_LOAD_BOOKING_REPORT),
      exhaustMap((payload: { bookingId: string; companyId: string}) => {
        return this.loadBookingReportData(payload.bookingId, payload.companyId);
      }),
    ),
  );

  private loadBookingReportData(
    bookingId: string,
    companyId: string
  ): Observable<Action> {
    return this.bookingService.getBookingReport(bookingId, companyId).pipe(
      pluck('body'),
      map(data => ({
        type: BookingActions.BOOKING_LOAD_BOOKING_REPORT_SUCCESS,
        payload: { report: data },
      })),
      catchError(err => {
        return of({
          type: BookingActions.BOOKING_LOAD_BOOKING_REPORT_FAILURE,
          payload: { error: err },
        });
      })
    );
  }


  $loadFileItinerary = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.BOOKINGS_LOAD_FILE_ITINERARY),
      exhaustMap((action: { bookingId: string; companyId: string}) => {
        return this.loadFileItinerariesData(action.bookingId, action.companyId);
      }),
    ),
  );

  private loadFileItinerariesData(
    bookingId: string, 
    companyId: string
  ): Observable<Action> {
    return this.bookingService.getAllFileItineraries(bookingId, companyId).pipe(
      pluck('body'),
      map(res => ({
        type: BookingActions.BOOKINGS_LOAD_FILE_ITINERARY_SUCCESS,
        payload: { fileItineraries: res },
      })),
    );
  }

  $loadBookingDocuments = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.BOOKING_LOAD_BOOKING_DOCUMENT_LIST),
      exhaustMap((payload: { fileId: string; companyId: string}) => {
        return this.loadBookingDocumentsData(payload.fileId, payload.companyId);
      }),
    ),
  );

  private loadBookingDocumentsData(
    fileId: string,
    companyId: string
  ): Observable<Action> {
    return this.bookingService.getBookingDocuments(fileId, companyId).pipe(
      pluck('body'),
      map(data => ({
        type: BookingActions.BOOKING_LOAD_BOOKING_DOCUMENT_LIST_SUCCESS,
        payload: { documents: data },
      })),
      catchError(err => {
        this.toastrService.danger(`No documents found`,'Failure');
        return of({
          type: BookingActions.BOOKING_LOAD_BOOKING_DOCUMENT_LIST_FAILURE,
          payload: { error: err },
        });
      })
    );
  }

  $downloadLink = createEffect(() =>
    this.actions$.pipe(
      ofType(BookingActions.BOOKINGS_DOWNLOAD_DOCUMENT),
      exhaustMap((payload: { id: string; companyId: string }) => {
        return this.downloadLink(payload.id, payload.companyId);
      }),
    ),
  );

  private downloadLink(id: string, companyId: string): Observable<Action> {
    return this.bookingService.downloadLink(id, companyId).pipe(
      pluck('body'),
      map(res => {
        return {
          type: BookingActions.BOOKINGS_DOWNLOAD_DOCUMENT_SUCCESS,
          payload: { documentLink: res },
        };
      }),
      catchError(err => {
        if (err.error && err.error.message && Array.isArray(err.error.message)) {
          err.error.message.forEach( error => {
            this.toastrService.danger(
              error.constraints.error,
              'Failure!',
            );
          })
        }
        else{
          this.toastrService.danger('Something went wrong try again later','failure')
        }
        return of({
          type: BookingActions.BOOKINGS_DOWNLOAD_DOCUMENT_FAILURE,
          payload: err,
        });
      }),
    );
  }

  constructor(
    private actions$: Actions,
    private bookingService: BookingService,
    private toastrService: NbToastrService,
    private translate: TranslateService,
  ) {}
}
